import type {
	GeomFingerprint,
	TopologyEdgeSourceDescriptor,
	TopologyEdgeSourceLocator,
	TopologyEdgeMatchHints,
} from '../../model/ExternalGeometrySourceDescriptor';
import {resolveExternalGeometrySourceRefEntityId} from '../../model/ExternalGeometrySourceDescriptor';
import {
	createExecutionDiagnostic,
	type ExecutionDiagnostic,
} from '../../model/SketchEntityReferenceDiagnostics';
import {TopologyReferenceIdentityPolicy} from './TopologyReferenceIdentityPolicy';
import type {
	TopologyReferenceIdentityDecision,
} from '../../../../../types/reference/sketch-entity-reference/TopologyReferenceIdentityPolicy';
import type {
	TopologyEdgeRelocationCandidate,
	TopologyEdgeRelocationHost,
	TopologyEdgeRelocationInput,
	TopologyEdgeRelocationResult,
	TopologyEdgeRelocationWorldPointTuple,
} from '../../../../../types/reference/sketch-entity-reference/TopologyEdgeRelocationService';

export type {
	TopologyEdgeEndpointAnchorRefs,
	TopologyEdgeRelocationCandidate,
	TopologyEdgeRelocationHost,
	TopologyEdgeRelocationInput,
	TopologyEdgeRelocationResult,
	TopologyEdgeRelocationWorldLine,
	TopologyEdgeRelocationWorldPointTuple,
} from '../../../../../types/reference/sketch-entity-reference/TopologyEdgeRelocationService';

/**
 * topology 边重定位服务。
 *
 * 说明：
 * 1. owner host 已限定来源特征与实体范围，本服务只在该范围内重定位；
 * 2. 当前快照存在同名 semanticName 时，先把候选限制在该来源族内；
 * 3. persistentRef、旧几何和完整相邻面只要仍能命中，就必须共同指向同一候选；
 * 4. subEntityId 与 index 只在已确认的身份层或来源族内消歧，不能单独恢复来源边。
 */
export class TopologyEdgeRelocationService {
	/** line 指纹校验允许的世界坐标浮点误差。 */
	private static readonly lineFingerprintTolerance = 1e-6;
	/** topology 身份证据统一裁决策略。 */
	private readonly identityPolicy = new TopologyReferenceIdentityPolicy();

	/**
	 * 解析可作为镜向轴的精确直边候选。
	 *
	 * 常态只复用 development 已验证安全的索引快速路径；任何成员血缘、语义族、
	 * 几何或相邻面证据存在时都回到完整重定位，避免热路径绕过统一身份裁决。
	 *
	 * @param descriptor 持久化的精确直边来源。
	 * @param host 当前 topology owner 运行时宿主。
	 * @returns 唯一安全候选；来源悬空、歧义或不是精确直边时返回 `null`。
	 */
	public relocateExactLineCandidateByDescriptor(
		descriptor: TopologyEdgeSourceDescriptor,
		host: TopologyEdgeRelocationHost,
	): TopologyEdgeRelocationCandidate | null {
		const locator = descriptor.locator;
		if (locator.curveKind !== 'line' || locator.resolutionLevel !== 'exact') {
			return null;
		}

		const indexedCandidate = host.getEdgeByIndex(locator.edgeIndex);
		if (
			indexedCandidate
			&& !indexedCandidate.topologyMemberId
			&& !locator.semanticName
			&& !locator.geomFingerprint
			&& !this.hasAdjacentFaceHints(locator.matchHints)
			&& this.matchesPersistentRef(locator, indexedCandidate)
			&& this.isCurveKindCompatible(locator, indexedCandidate)
		) {
			return indexedCandidate;
		}

		const relocation = this.relocate({descriptor, host});
		return relocation.status === 'resolved' ? relocation.edge : null;
	}

	/**
	 * 根据持久化 descriptor 与当前 topology host 重定位来源边。
	 *
	 * @param input 重定位输入。
	 * @returns 命中时返回候选边；无法安全命中时返回诊断。
	 */
	public relocate(input: TopologyEdgeRelocationInput): TopologyEdgeRelocationResult {
		const {descriptor, host} = input;
		const locator = descriptor.locator;
		const hostIndexedCandidate = host.getEdgeByIndex(locator.edgeIndex);

		if (
			!this.hasStableSignals(locator)
			&& !this.hasAdjacentFaceHints(locator.matchHints)
			&& !locator.semanticName
		) {
			const diagnostic = this.createSourceMissingDiagnostic(descriptor);
			return {
				status: 'failed',
				diagnostic,
			};
		}

		/*
		 * 没有语义范围时，旧 index 位置上的 persistentRef 完整命中可以直接确认。
		 * 保存了 semanticName 时必须先扫描 owner 内的语义族，避免快速路径越过
		 * 明确的谱系冲突，绑定到复用了旧 persistentRef 的其它边。
		 */
		if (
			hostIndexedCandidate
			&& !hostIndexedCandidate.topologyMemberId
			&& !locator.semanticName
			&& !locator.geomFingerprint
			&& !this.hasAdjacentFaceHints(locator.matchHints)
			&& this.matchesPersistentRef(locator, hostIndexedCandidate)
			&& this.isCurveKindCompatible(locator, hostIndexedCandidate)
		) {
			return {
				status: 'resolved',
				edge: hostIndexedCandidate,
			};
		}

		const edges = host.listEdges();
		const hasTopologyMemberScope = edges.some((edge) => Boolean(edge.topologyMemberId));
		const preferredMemberEdges = this.findPreferredTopologyMemberEdges(
			descriptor,
			edges,
		);
		const sourceTopologyMemberId = this.resolveSourceTopologyMemberId(descriptor);
		const sourceTopologyMemberMissing = hasTopologyMemberScope
			&& Boolean(sourceTopologyMemberId)
			&& preferredMemberEdges.length === 0;
		const localEdges = preferredMemberEdges.length > 0
			? preferredMemberEdges
			: edges;
		/*
		 * 组合 checkpoint host 中，同一个 index 可以在多个结果体成员中重复。
		 * 只有已锁定当前成员或旧 host 没有成员信息时，index 快速读取才具有局部意义。
		 */
		const indexedCandidate = preferredMemberEdges.length > 0
			? this.findUniqueIndexedCandidate(localEdges, locator.edgeIndex)
			: hasTopologyMemberScope
				? null
				: hostIndexedCandidate;
		/*
		 * 普通单边引用没有“结果体成员消失后自动继承到其它成员”的语义。
		 * 只有旧成员仍存在时，才允许全血缘证据识别该边迁移到了分裂成员；
		 * 旧成员已经消失则必须悬空，不能把相似边解释为原边。
		 */
		const relocationDecision = sourceTopologyMemberMissing
			? {status: 'missing'} as const
			: hasTopologyMemberScope
				? this.resolveAcrossTopologyMembers(descriptor, locator, edges)
				: this.resolveRelocatedEdge({
					locator,
					edges: localEdges,
				});

		if (relocationDecision.status !== 'resolved') {
			/*
			 * checkpoint 已经把同一 modelBody 血缘的多个结果体展开为统一候选集，
			 * 不能再用“旧 index + 任意一个相邻面”抢先返回原成员；否则分裂迁移
			 * 会绑定到仍然存在的其它物理边。旧场景 host 仍保留既有兼容路径。
			 */
			const indexedFallbackEdge = relocationDecision.status === 'missing'
				&& !hasTopologyMemberScope
				? this.resolveFeatureResultIndexedFallback({
					descriptor,
					locator,
					indexedCandidate,
					edges: localEdges,
				})
				: null;
			if (indexedFallbackEdge) {
				return {
					status: 'resolved',
					edge: indexedFallbackEdge,
				};
			}

			const diagnostic = relocationDecision.status === 'ambiguous'
				? this.createSourceAmbiguousDiagnostic(descriptor)
				: this.createSourceMissingDiagnostic(
					descriptor,
					sourceTopologyMemberMissing
						? '来源结果体成员已经消失，普通边不允许跨成员猜测重绑定。'
						: undefined,
				);
			return {status: 'failed', diagnostic};
		}

		return {
			status: 'resolved',
			edge: relocationDecision.candidate,
		};
	}

	/**
	 * 提取与来源旧结果对象一致的 checkpoint 成员候选。
	 *
	 * `resultObjectId` 是新记录的精确来源成员；旧记录缺少它时，
	 * `modelBodyRevisionId` 按统一实体约定等于创建引用时的结果对象 ID，可作为兼容锚点。
	 * 二者都只限定来源物理成员，不会把 `modelBodyId` 误当成边级身份。
	 *
	 * @param descriptor 持久化来源描述。
	 * @param edges 当前同血缘候选。
	 * @returns 旧结果体成员内的候选；没有该成员时返回空数组。
	 */
	private findPreferredTopologyMemberEdges(
		descriptor: TopologyEdgeSourceDescriptor,
		edges: readonly TopologyEdgeRelocationCandidate[],
	): TopologyEdgeRelocationCandidate[] {
		const sourceTopologyMemberId = this.resolveSourceTopologyMemberId(descriptor);
		if (!sourceTopologyMemberId) {
			return [];
		}
		return edges.filter((edge) => edge.topologyMemberId === sourceTopologyMemberId);
	}

	/**
	 * 读取普通边创建时所在的精确结果体成员。
	 *
	 * `modelBodyId` 只表达跨结果体血缘，不能代替成员身份；兼容旧记录时仅允许使用
	 * 按系统约定等于 GeometryObject.id 的 `modelBodyRevisionId`。
	 *
	 * @param descriptor 持久化来源描述。
	 * @returns 精确成员 ID；记录不具备成员锚点时返回 null。
	 */
	private resolveSourceTopologyMemberId(
		descriptor: TopologyEdgeSourceDescriptor,
	): string | null {
		const resultObjectId = descriptor.owner.resultObjectId?.trim();
		if (resultObjectId) {
			return resultObjectId;
		}
		const modelBodyRevisionId = descriptor.owner.modelBodyRevisionId?.trim();
		return modelBodyRevisionId || null;
	}

	/**
	 * 在已经限定到单个结果体成员后读取唯一 index 候选。
	 *
	 * @param edges 单个成员内的候选集合。
	 * @param edgeIndex 当前边索引。
	 * @returns 唯一 index 候选；重复或缺失时返回 null。
	 */
	private findUniqueIndexedCandidate(
		edges: readonly TopologyEdgeRelocationCandidate[],
		edgeIndex: number,
	): TopologyEdgeRelocationCandidate | null {
		const matches = edges.filter((edge) => edge.edgeIndex === edgeIndex);
		return matches.length === 1 ? matches[0] ?? null : null;
	}

	/**
	 * 在同一 modelBody 血缘的多个结果体成员之间安全重定位。
	 *
	 * 每个结果体成员的 Puppy CAD 内核 topology 都独立编号，因此 edgeIndex 与 subEntityId
	 * 只能视为同一条“局部地址”证据，不能重复计票。算法先由物理引用、几何与
	 * 完整相邻面限定候选，再使用局部地址识别分裂迁移；只有仍然存在歧义时，
	 * 才把旧 resultObjectId 当作成员偏好。
	 *
	 * @param descriptor 持久化来源描述。
	 * @param locator 持久化边定位信息。
	 * @param edges 同一 modelBody 血缘内的全部候选。
	 * @returns 唯一可信的跨成员候选；无法确认时返回 null。
	 */
	private resolveAcrossTopologyMembers(
		descriptor: TopologyEdgeSourceDescriptor,
		locator: TopologyEdgeSourceLocator,
		edges: TopologyEdgeRelocationCandidate[],
	): TopologyReferenceIdentityDecision<TopologyEdgeRelocationCandidate> {
		const compatibleEdges = edges.filter((edge) => this.isCurveKindCompatible(locator, edge));
		const semanticFamilyEdges = locator.semanticName
			? compatibleEdges.filter((edge) => edge.semanticName === locator.semanticName)
			: [];
		const identityScope = semanticFamilyEdges.length > 0
			? semanticFamilyEdges
			: compatibleEdges.filter((edge) => this.isSemanticNameCompatible(locator, edge));
		const adjacentFaceRefs = locator.matchHints?.adjacentFaceRefs;
		const edgeIdentityEvidenceGroups = [
			this.findPersistentRefCandidates(locator, identityScope),
			identityScope.filter((candidate) => (
				this.matchesGeometryFingerprint(locator.geomFingerprint, candidate)
			)),
			adjacentFaceRefs && adjacentFaceRefs.length > 0
				? identityScope.filter((candidate) => (
					this.containsAll(candidate.owningFaceRefs, adjacentFaceRefs)
				))
				: [],
		].filter((group) => group.length > 0);
		const preferredMemberEdges = this.findPreferredTopologyMemberEdges(
			descriptor,
			identityScope,
		);
		/*
		 * 边级物理证据存在时允许它识别跨成员迁移，旧成员只作为最终偏好；
		 * 边级物理证据全部失效时，精确旧成员可作为已确认范围，让局部地址仅在
		 * 该成员内部消歧。它不能参与跨成员匹配，也不会把成员身份冒充为边身份。
		 */
		const independentEvidenceGroups = edgeIdentityEvidenceGroups.length > 0
			? edgeIdentityEvidenceGroups
			: preferredMemberEdges.length > 0
				? [preferredMemberEdges]
				: [];
		const localAddressCandidates = this.findLocalAddressCandidates(locator, identityScope);
		const identityDecision = this.identityPolicy.resolve({
			candidates: identityScope,
			independentEvidenceGroups,
			localAddressCandidates,
		});
		if (identityDecision.status !== 'resolved') {
			return identityDecision;
		}

		const memberPreferenceScope = [identityDecision.candidate];
		const preferredMemberCandidates = this.findPreferredTopologyMemberEdges(
			descriptor,
			memberPreferenceScope,
		);
		return {
			status: 'resolved',
			candidate: preferredMemberCandidates[0] ?? identityDecision.candidate,
		};
	}

	/**
	 * 按语义范围、物理身份和相邻面信号寻找唯一候选。
	 *
	 * @param input 当前 locator 与全量候选。
	 * @returns 唯一可信候选；不存在或歧义时返回 null。
	 */
	private resolveRelocatedEdge(input: {
		locator: TopologyEdgeSourceLocator;
		edges: TopologyEdgeRelocationCandidate[];
	}): TopologyReferenceIdentityDecision<TopologyEdgeRelocationCandidate> {
		const {locator, edges} = input;
		const compatibleEdges = edges.filter((edge) => this.isCurveKindCompatible(locator, edge));

		/*
		 * semanticName 只在当前快照确实提供同名族时限定候选域。若生成快照暂时
		 * 缺失语义字段，则保留无语义候选给 persistentRef、几何和邻接证据判断；
		 * 明确属于其它语义族的候选始终排除。
		 */
		const semanticFamilyEdges = locator.semanticName
			? compatibleEdges.filter((edge) => edge.semanticName === locator.semanticName)
			: [];
		const identityScope = semanticFamilyEdges.length > 0
			? semanticFamilyEdges
			: compatibleEdges.filter((edge) => this.isSemanticNameCompatible(locator, edge));

		const persistentRefMatches = this.findPersistentRefCandidates(locator, identityScope);
		const geometryMatches = identityScope.filter((candidate) => (
			this.matchesGeometryFingerprint(locator.geomFingerprint, candidate)
		));
		const adjacentFaceMatches = this.findAdjacentFaceReferenceCandidates(locator, identityScope);
		const primaryEvidenceGroups = [
			persistentRefMatches,
			geometryMatches,
			adjacentFaceMatches,
		].filter((group) => group.length > 0);
		const identityDecision = this.identityPolicy.resolve({
			candidates: identityScope,
			independentEvidenceGroups: primaryEvidenceGroups,
			localAddressCandidates: this.findLocalAddressCandidates(locator, identityScope),
		});
		return identityDecision;
	}

	/**
	 * 在特征结果重建后使用同一 owner 内的旧索引做受控兼容。
	 *
	 * @param input 当前来源、旧索引候选与全量候选。
	 * @returns 可以安全兼容时返回候选，否则返回 null。
	 */
	private resolveFeatureResultIndexedFallback(input: {
		descriptor: TopologyEdgeSourceDescriptor;
		locator: TopologyEdgeSourceLocator;
		indexedCandidate: TopologyEdgeRelocationCandidate | null;
		edges: TopologyEdgeRelocationCandidate[];
	}): TopologyEdgeRelocationCandidate | null {
		const {descriptor, locator, indexedCandidate, edges} = input;
		if (
			descriptor.owner.ownerKind !== 'feature-result'
			|| !indexedCandidate
			|| !this.hasStableSignals(locator)
		) {
			return null;
		}
		if (
			this.findPersistentRefCandidates(locator, edges).length > 0
		) {
			return null;
		}
		if (
			locator.semanticName
			&& edges.some((edge) => edge.semanticName === locator.semanticName)
		) {
			return null;
		}
		if (this.findAdjacentFaceReferenceCandidates(locator, edges).length > 1) {
			return null;
		}
		if (
			locator.semanticName
			&& indexedCandidate.semanticName
			&& indexedCandidate.semanticName !== locator.semanticName
		) {
			return null;
		}
		/*
		 * edgeIndex 只表示当前快照位置，布尔重建后可能已被其他同类型边占用。
		 * 至少保留一个旧相邻面身份，才能证明当前 index 候选仍属于原边的局部拓扑。
		 */
		if (!this.matchesAnyAdjacentFaceReference(locator.matchHints, indexedCandidate)) {
			return null;
		}
		return this.hasRequiredProjectionPayload(locator, indexedCandidate)
			? indexedCandidate
			: null;
	}

	/**
	 * 判断来源是否保存了旧物理身份。
	 *
	 * @param locator 持久化边定位信息。
	 * @returns 存在 persistentRef 或 subEntityId 时返回 true。
	 */
	private hasStableSignals(locator: TopologyEdgeSourceLocator): boolean {
		return typeof locator.persistentRef === 'string'
			|| typeof locator.subEntityId === 'string';
	}

	/**
	 * 判断候选是否命中来源的 persistentRef。
	 *
	 * @param locator 持久化边定位信息。
	 * @param candidate 当前候选边。
	 * @returns persistentRef 存在且相等时返回 true。
	 */
	private matchesPersistentRef(
		locator: TopologyEdgeSourceLocator,
		candidate: TopologyEdgeRelocationCandidate,
	): boolean {
		return typeof locator.persistentRef === 'string'
			&& candidate.persistentRef === locator.persistentRef;
	}

	/**
	 * 判断候选是否命中来源的 subEntityId。
	 *
	 * @param locator 持久化边定位信息。
	 * @param candidate 当前候选边。
	 * @returns subEntityId 存在且相等时返回 true。
	 */
	private matchesSubEntityId(
		locator: TopologyEdgeSourceLocator,
		candidate: TopologyEdgeRelocationCandidate,
	): boolean {
		return typeof locator.subEntityId === 'string'
			&& candidate.subEntityId === locator.subEntityId;
	}

	/**
	 * 在全量候选中扫描 persistentRef。
	 *
	 * @param locator 持久化边定位信息。
	 * @param edges 当前 owner 候选。
	 * @returns persistentRef 匹配的候选集合。
	 */
	private findPersistentRefCandidates(
		locator: TopologyEdgeSourceLocator,
		edges: TopologyEdgeRelocationCandidate[],
	): TopologyEdgeRelocationCandidate[] {
		if (!locator.persistentRef) {
			return [];
		}
		return edges.filter((edge) => this.matchesPersistentRef(locator, edge));
	}

	/**
	 * 判断 index 派生身份是否与已知语义谱系冲突。
	 *
	 * subEntityId 当前包含 topology index，旧位置可能在重建后被另一条边复用。
	 * 两侧都具有 semanticName 且明确不同时，必须淘汰该弱身份候选；任一侧缺失
	 * 语义时继续交由其它证据判断，避免语义暂时丢失造成不跟随。
	 *
	 * @param locator 持久化边定位信息。
	 * @param candidate 当前候选边。
	 * @returns 没有已知语义冲突时返回 true。
	 */
	private isSemanticNameCompatible(
		locator: TopologyEdgeSourceLocator,
		candidate: TopologyEdgeRelocationCandidate,
	): boolean {
		return !locator.semanticName
			|| !candidate.semanticName
			|| locator.semanticName === candidate.semanticName;
	}

	/**
	 * 返回完整持久相邻面身份匹配的候选集合。
	 *
	 * @param locator 持久化边定位信息。
	 * @param edges 当前 owner 候选。
	 * @returns 完整持久相邻面匹配集合；局部面地址不在此升级为物理身份。
	 */
	private findAdjacentFaceReferenceCandidates(
		locator: TopologyEdgeSourceLocator,
		edges: TopologyEdgeRelocationCandidate[],
	): TopologyEdgeRelocationCandidate[] {
		const adjacentFaceRefs = locator.matchHints?.adjacentFaceRefs;
		if (!adjacentFaceRefs || adjacentFaceRefs.length === 0) {
			return [];
		}
		return edges.filter((edge) => (
			this.containsAll(edge.owningFaceRefs, adjacentFaceRefs)
		));
	}

	/**
	 * 在已有独立身份范围内收集局部地址候选。
	 *
	 * subEntityId 与 edgeIndex 属于同一局部地址通道，优先使用更明确的 subEntityId；
	 * 相邻面 subEntityId 只进一步收窄该局部集合，不能在集合为空时独立恢复候选。
	 *
	 * @param locator 持久化边定位信息。
	 * @param candidates 已由 owner、成员或来源族限定的候选。
	 * @returns 可用于最终消歧的局部地址候选。
	 */
	private findLocalAddressCandidates(
		locator: TopologyEdgeSourceLocator,
		candidates: TopologyEdgeRelocationCandidate[],
	): TopologyEdgeRelocationCandidate[] {
		const subEntityCandidates = locator.subEntityId
			? candidates.filter((candidate) => this.matchesSubEntityId(locator, candidate))
			: [];
		const localAddressCandidates = subEntityCandidates.length > 0
			? subEntityCandidates
			: candidates.filter((candidate) => candidate.edgeIndex === locator.edgeIndex);
		const adjacentFaceSubEntityIds = locator.matchHints?.adjacentFaceSubEntityIds;
		if (!adjacentFaceSubEntityIds || adjacentFaceSubEntityIds.length === 0) {
			return localAddressCandidates;
		}
		return localAddressCandidates.filter((candidate) => (
			this.containsAll(
				candidate.adjacentFaceSubEntityIds,
				adjacentFaceSubEntityIds,
			)
		));
	}

	/**
	 * 判断身份候选是否与来源曲线类型兼容。
	 *
	 * 已知类型不一致时必须拒绝，避免 line 与 arc/circle 互绑；旧记录或轻量快照
	 * 缺少类型时不把“未知”解释为冲突，最终仍需其它身份或拓扑证据确认候选。
	 *
	 * @param locator 持久化边定位信息。
	 * @param candidate 当前候选边。
	 * @returns 类型未知或严格一致时返回 true。
	 */
	private isCurveKindCompatible(
		locator: TopologyEdgeSourceLocator,
		candidate: TopologyEdgeRelocationCandidate,
	): boolean {
		return typeof locator.curveKind !== 'string'
			|| typeof candidate.curveKind !== 'string'
			|| locator.curveKind === candidate.curveKind;
	}

	/**
	 * 判断候选是否具有当前解析等级所需的投影载荷。
	 *
	 * @param locator 持久化边定位信息。
	 * @param candidate 当前候选边。
	 * @returns 当前解析等级与候选载荷兼容时返回 true。
	 */
	private hasRequiredProjectionPayload(
		locator: TopologyEdgeSourceLocator,
		candidate: TopologyEdgeRelocationCandidate,
	): boolean {
		if (candidate.exactConic && this.isSameKnownCurveKind(locator.curveKind, candidate.curveKind)) {
			return true;
		}
		if (
			locator.resolutionLevel === 'exact'
			&& locator.curveKind === 'line'
			&& candidate.curveKind === 'line'
			&& Boolean(candidate.worldLine)
		) {
			return true;
		}
		if (
			locator.resolutionLevel === 'sampled-only'
			&& this.isSameKnownCurveKind(locator.curveKind, candidate.curveKind)
			&& Array.isArray(candidate.sampledPointWorlds)
			&& candidate.sampledPointWorlds.length >= 2
		) {
			return true;
		}
		if (
			locator.resolutionLevel === 'fitted-exact'
			&& Boolean(locator.fitMetadata)
			&& this.isSameKnownCurveKind(locator.curveKind, candidate.curveKind)
			&& Array.isArray(candidate.sampledPointWorlds)
			&& candidate.sampledPointWorlds.length >= 2
		) {
			return true;
		}
		return false;
	}

	/**
	 * 判断受约束 index 两侧是否属于同一已知 Puppy CAD 内核曲线类型。
	 *
	 * 任一侧类型未知时都不能让 index 升级为身份；这可避免把非直线边的端点弦线
	 * 误认为旧直线，也避免 sampled 曲线跨类型漂移。
	 *
	 * @param sourceCurveKind 创建引用时保存的曲线类型。
	 * @param candidateCurveKind 当前 index 候选的曲线类型。
	 * @returns 两侧均已知且严格相同时返回 true。
	 */
	private isSameKnownCurveKind(
		sourceCurveKind: TopologyCurveKind | undefined,
		candidateCurveKind: TopologyCurveKind | undefined,
	): boolean {
		return typeof sourceCurveKind === 'string'
			&& typeof candidateCurveKind === 'string'
			&& sourceCurveKind === candidateCurveKind;
	}

	/**
	 * 判断 descriptor 是否携带相邻面提示。
	 *
	 * @param hints 持久化的相邻面提示。
	 * @returns 至少存在一类非空相邻面提示时返回 true。
	 */
	private hasAdjacentFaceHints(hints: TopologyEdgeMatchHints | undefined): hints is TopologyEdgeMatchHints {
		return Boolean(
			(hints?.adjacentFaceRefs && hints.adjacentFaceRefs.length > 0)
			|| (hints?.adjacentFaceSubEntityIds && hints.adjacentFaceSubEntityIds.length > 0),
		);
	}

	/**
	 * 判断受约束 index 候选是否保留至少一个持久相邻面身份。
	 *
	 * 完整相邻面组合可能在合法切除重建后局部变化，因此 index 兼容只要求至少一个
	 * 持久面引用继续成立；局部面地址不能把 index 升级为身份。
	 *
	 * @param hints 创建引用时保存的相邻面身份。
	 * @param candidate 当前 index 位置的候选边。
	 * @returns 任一持久面引用命中时返回 true。
	 */
	private matchesAnyAdjacentFaceReference(
		hints: TopologyEdgeMatchHints | undefined,
		candidate: TopologyEdgeRelocationCandidate,
	): boolean {
		if (!hints?.adjacentFaceRefs || hints.adjacentFaceRefs.length === 0) {
			return false;
		}
		return hints.adjacentFaceRefs.some((faceRef) => (
			candidate.owningFaceRefs?.includes(faceRef)
		));
	}

	/**
	 * 判断候选集合是否包含所有必需值。
	 *
	 * @param candidateValues 候选边上的值集合。
	 * @param requiredValues descriptor 中持久化的必需值集合。
	 * @returns required 为空或全部被 candidate 包含时返回 true。
	 */
	private containsAll(
		candidateValues: string[] | undefined,
		requiredValues: string[] | undefined,
	): boolean {
		if (!requiredValues || requiredValues.length === 0) {
			return true;
		}

		if (!candidateValues || candidateValues.length === 0) {
			return false;
		}

		return requiredValues.every((value) => candidateValues.includes(value));
	}

	/**
	 * 判断候选边是否命中已保存的几何指纹。
	 *
	 * @param fingerprint 持久化几何指纹。
	 * @param candidate 当前候选边。
	 * @returns line 端点在容差内双向一致时返回 true；缺失或不支持时不参与。
	 */
	private matchesGeometryFingerprint(
		fingerprint: GeomFingerprint | undefined,
		candidate: TopologyEdgeRelocationCandidate,
	): boolean {
		if (!fingerprint || fingerprint.kind !== 'line') {
			return false;
		}

		if (!candidate.worldLine) {
			return false;
		}

		const sameDirection = this.pointsEqual(fingerprint.startWorld, candidate.worldLine.start)
			&& this.pointsEqual(fingerprint.endWorld, candidate.worldLine.end);
		const reversedDirection = this.pointsEqual(fingerprint.startWorld, candidate.worldLine.end)
			&& this.pointsEqual(fingerprint.endWorld, candidate.worldLine.start);
		return sameDirection || reversedDirection;
	}

	/**
	 * 比较两个世界坐标是否在 line 指纹容差内一致。
	 *
	 * @param left 左侧坐标。
	 * @param right 右侧坐标。
	 * @returns 三个坐标分量均在容差内时返回 true。
	 */
	private pointsEqual(
		left: TopologyEdgeRelocationWorldPointTuple,
		right: TopologyEdgeRelocationWorldPointTuple,
	): boolean {
		return Math.abs(left[0] - right[0]) <= TopologyEdgeRelocationService.lineFingerprintTolerance
			&& Math.abs(left[1] - right[1]) <= TopologyEdgeRelocationService.lineFingerprintTolerance
			&& Math.abs(left[2] - right[2]) <= TopologyEdgeRelocationService.lineFingerprintTolerance;
	}

	/**
	 * 创建无法找到来源边的诊断。
	 *
	 * @param descriptor 持久化来源描述。
	 * @param reason 当前重定位失败原因。
	 * @returns 可汇总到上层流程的 warning 诊断。
	 */
	private createSourceMissingDiagnostic(
		descriptor: TopologyEdgeSourceDescriptor,
		reason = '当前 owner 中找不到可安全匹配的候选边。',
	): ExecutionDiagnostic {
		return createExecutionDiagnostic({
			kind: 'source-missing',
			level: 'warning',
			message: `拓扑来源边重定位失败：${reason}`,
			sourceRefEntityId: resolveExternalGeometrySourceRefEntityId(descriptor),
		});
	}

	/**
	 * 创建来源边存在冲突证据或多个等价候选的诊断。
	 *
	 * @param descriptor 持久化来源描述。
	 * @returns 可持久化到 dangling 引用的 warning 诊断。
	 */
	private createSourceAmbiguousDiagnostic(
		descriptor: TopologyEdgeSourceDescriptor,
	): ExecutionDiagnostic {
		return createExecutionDiagnostic({
			kind: 'source-ambiguous',
			level: 'warning',
			message: '拓扑来源边存在冲突证据或多个等价候选，已禁止猜测重绑定。',
			sourceRefEntityId: resolveExternalGeometrySourceRefEntityId(descriptor),
		});
	}

}
